<template>
  <main class="container">
    <section class="container__info">
      <p class="container__info__title">Adicione imagens dos pedidos médicos</p>

      <p class="container__info__message">
        {{ subtitle }}
      </p>
      <div class="file-area" @click="$emit('main-action')" v-if="!files.length">
        <FileTextSvg class="file-area__icon file-area__icon--file" />
        <strong class="file-area__title">Adicionar pedido médico</strong>
      </div>

      <DrawerExplanationComponent
        :open="showDrawer"
        @close="showDrawer = false"
        @toggle="showDrawer = false"
      >
        <template #content>
          <section class="drawer-info">
            <h2 class="drawer-info__title">Por que preciso levar o original se já enviei aqui?</h2>
            <p class="drawer-info__text">
              O convênio exige que o pedido médico original seja entregue assinado pelo paciente
              para avaliar a cobertura dos exames.
            </p>
            <p class="drawer-info__text">
              Ao enviar os documentos durante o Check-in, preparamos seu atendimento na unidade, mas
              ainda é necessário levar o documento original.
            </p>
          </section>
        </template>
      </DrawerExplanationComponent>

      <div class="files" v-if="files.length">
        <p class="files__title">Arquivos adicionados</p>
        <ul class="files__list">
          <li
            v-for="(file, index) in files"
            class="files__item"
            :class="{
              'files__item--loading': isUploading(file),
            }"
            :key="`${file.data.name}-${index}`"
          >
            <div
              class="files__item--box"
              :class="{
                'files__item--error': isError(file),
              }"
            >
              <div class="files__icon-holder" v-if="file.data.status !== 'UPLOADING'">
                <FileNameBrokenSvg v-if="isError(file)" class="files__icon" />
                <FileNameSvg v-else class="files__icon" />

                <span class="files__icon-extension">{{ getFileExtension(file.data.name) }}</span>
              </div>

              <div class="files__spinner" v-else>
                <SpinnerSvg />
              </div>

              <span
                class="files__name"
                :class="{
                  'files__name--error': isError(file),
                }"
                >{{ setFileName(file.data.name) }}</span
              >
              <TrashSvg @click="$emit('remove', index)" />
            </div>

            <div class="files__error" v-if="isError(file)">
              <AlertCircleStrokeSvg />
              <p>Envie arquivo no formato jpg, jpeg, png ou pdf</p>
            </div>
          </li>
        </ul>
      </div>

      <div class="file-area file-area--small" @click="$emit('main-action')" v-if="files.length">
        <strong class="file-area__title">Adicionar outro arquivo</strong>
        <FileTextSvg class="file-area__icon file-area__icon--file" />
      </div>
      <CalloutMessage @explain-click="showDrawer = true" />
    </section>

    <section class="bottomContainer" v-if="files.length">
      <button class="button" @click="$emit('send-files')" :disabled="disabledSendFiles">
        Enviar pedido médico
      </button>
    </section>

    <section class="bottomContainer" v-if="!files.length || disabledSendFiles">
      <button class="button button--secondary" @click="$emit('secondary-action')">
        Não tenho pedido médico
      </button>
    </section>
  </main>
</template>

<script>
import CalloutMessage from './CalloutMessage.partial.vue';
import DrawerExplanationComponent from '@/components/drawerExplanation.component.vue';
export default {
  name: 'InitialViewOCR',
  components: {
    CalloutMessage,
    DrawerExplanationComponent,
  },
  props: {
    files: [],
    subtitle: {
      type: String,
      default: 'Vamos realizar a leitura dos seus exames.',
    },
  },
  data() {
    return {
      showDrawer: false,
    };
  },

  computed: {
    disabledSendFiles() {
      const hasUploadingStatus = this.files.some((file) => file.data.status === 'UPLOADING');
      const allFilesHaveErrorStatus = this.files.every((file) => file.data.status === 'ERROR');

      return hasUploadingStatus || allFilesHaveErrorStatus;
    },
  },

  methods: {
    getFileExtension(file) {
      if (file) {
        return file.split('.').pop();
      }
    },
    setFileName(file) {
      if (file?.length > 35) {
        return `${file.substring(0, 35)}...${this.getFileExtension(file)}`;
      }
      return file;
    },

    isUploading(file) {
      return file.data.status === 'UPLOADING';
    },

    isError(file) {
      return file.data.status === 'ERROR';
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  align-items: center;
  align-content: center;
  min-width: 320px;

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 425px;
    overflow-y: auto;
    padding-bottom: 16px;

    &__title {
      font-family: 'Dasa sans', sans-serif;
      font-weight: var(--font-weight-extra-bold);
      font-size: 16px;
      line-height: 24px;
      text-align: left;
      color: #161616;
    }

    &__message {
      font-family: 'Dasa sans', sans-serif;
      font-weight: var(--font-weight-normal);
      font-size: 14px;
      line-height: 24px;
      text-align: left;
      color: #161616;
    }
  }
}

.button {
  font-family: 'Dasa sans', sans-serif;
  font-size: 16px;
  font-weight: var(--font-weight-bold);
  line-height: 24px;
  text-align: center;
  width: 100%;
  padding: 12px 0px;
  border: 1px solid var(--color-button-primary);
  border-radius: 4px;
  background: var(--color-button-primary);
  margin: 0 0 16px 0;
  color: white;
  cursor: pointer;

  &--secondary {
    margin: 0;
    background: white;
    color: var(--color-button-primary);

    & svg {
      vertical-align: middle;
      margin-left: 10px;

      path {
        fill: var(--color-primary);
      }
    }
  }

  &:disabled {
    cursor: not-allowed;
    background-color: var(--color-grey-1);
    border-color: var(--color-grey-5);
    color: var(--color-grey-5);
  }
}

.file-area {
  padding: 32px 10px;
  border-radius: 4px;
  text-align: left;
  margin: 32px 0 24px;
  border: 1px solid #1616163d;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: 0.5s background;

  &--small {
    padding: 8px 10px;
    flex-direction: row;
    margin-top: 0;

    .file-area__title {
      color: #0037ff;
    }

    .file-area__icon path {
      fill: #0037ff;
    }

    .file-area__icon--file {
      transform: scale(0.5);
    }
  }
  &:hover {
    background-color: #e0e7ff;

    .file-area__title {
      color: #0037ff;
    }

    .file-area__icon path {
      fill: #0037ff;
    }
  }

  &__title {
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    color: #0037ff;
  }

  &__icon {
    width: 24px;
    height: 24px;
    vertical-align: middle;

    &--file {
      transform: scale(0.75) translateX(-6px);
      width: auto;
      height: auto;
    }

    path {
      fill: #0037ff;
    }
  }

  span.list__title--spaced {
    margin-left: -6px;
  }
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}

.files {
  max-width: 425px;
  width: 100%;
  margin-top: 24px;
  &__list {
    margin-top: 16px;
    max-height: 330px;
    overflow: auto;
  }
  &__spinner {
    width: 28px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    animation: rotateSpinner 1s linear infinite;
  }
  &__item {
    margin-bottom: 16px;

    &--box {
      border-radius: $border-radius;
      background: #e6e6e6;
      padding: 9px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      position: relative;
      transition: 0.5s all;
    }

    &--loading {
      opacity: 0.6;
      &::after {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        transform: translateX(-100%);
        background-image: linear-gradient(
          90deg,
          rgba(#fff, 0) 0,
          rgba(#fff, 0.2) 20%,
          rgba(#fff, 0.5) 60%,
          rgba(#fff, 0)
        );
        animation: shimmer 1s infinite;
        content: '';
      }
    }

    &--error {
      border-radius: 8px 8px 0 0;
    }
  }
  &__name {
    color: #363636;
    font-size: 14px;
    line-height: 170%;
    text-overflow: ellipsis;
    line-clamp: 1;
    -webkit-line-clamp: 1;
    display: -webkit-box;
    overflow: hidden;
    word-wrap: break-word;
    max-height: 23px;
    text-align: left;
    width: 100%;
    max-width: 80%;

    &--error {
      text-decoration: line-through;
    }
  }
  &__title {
    color: #161616;
    font-weight: 700;
    font-size: 14px;
    line-height: 115%;
    text-align: left;
    display: block;
  }
  &__icon {
    width: 28px;
    height: 32px;
    position: relative;

    &-extension {
      color: #363636;
      text-transform: uppercase;
      font-weight: 700;
      font-size: 10px;
      line-height: 100%;
      position: absolute;
      bottom: 4px;
      left: 50%;
      transform: translateX(-50%);
    }

    &-holder {
      position: relative;
    }
  }

  &__error {
    height: 24px;
    padding: 0 16px;
    position: relative;
    display: flex;
    align-items: center;
    border-radius: 0 0 8px 8px;
    background-color: #fef1f2;

    p {
      margin-left: 4px;
      font-size: 12px;
      color: #bc293a;
    }
  }

  @keyframes rotateSpinner {
    to {
      transform: rotate(360deg);
    }
  }
}

.bottomContainer {
  display: block;
  width: 100%;
  max-width: 425px;
}
</style>
